import React, { useState } from 'react'
import ButtonBase from '@mui/material/ButtonBase'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import CheckIcon from '@mui/icons-material/Check'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useTranslation } from 'react-i18next'
import { LANGUAGES, LanguageCode } from 'translations'
import { useLanguageSelectorStyles } from './LanguageSelector.styles'

/**
 * Language switcher rendered in the application header. The available languages
 * and their flags come from the registry in `src/translations/index.ts`.
 */
const LanguageSelector: React.FC = () => {
    const { t, i18n } = useTranslation('common')
    const styles = useLanguageSelectorStyles()
    const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null)

    const isOpen = Boolean(anchorElement)
    const activeLanguage =
        LANGUAGES.find(({ code }) => code === i18n.resolvedLanguage) ??
        LANGUAGES[0]

    const openMenu = (event: React.MouseEvent<HTMLElement>) =>
        setAnchorElement(event.currentTarget)

    const closeMenu = () => setAnchorElement(null)

    const selectLanguage = (code: LanguageCode) => {
        if (code !== i18n.resolvedLanguage) {
            i18n.changeLanguage(code)
        }
        closeMenu()
    }

    return (
        <>
            <ButtonBase
                className={styles.trigger}
                onClick={openMenu}
                aria-label={t('language.select')}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                id="language-selector-button"
            >
                <img
                    className={styles.flag}
                    src={activeLanguage.flag}
                    alt=""
                    aria-hidden="true"
                />
                <span className={styles.triggerLabel}>
                    {activeLanguage.shortName}
                </span>
                <KeyboardArrowDownIcon
                    className={`${styles.chevron} ${isOpen ? 'open' : ''}`}
                />
            </ButtonBase>

            <Menu
                anchorEl={anchorElement}
                open={isOpen}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: { className: styles.menuPaper },
                    list: {
                        'aria-labelledby': 'language-selector-button',
                        role: 'listbox'
                    },
                    // The theme paints every backdrop dark, which would dim the
                    // whole page behind this dropdown.
                    backdrop: { sx: { backgroundColor: 'transparent' } }
                }}
            >
                {LANGUAGES.map(({ code, name, flag }) => {
                    const isSelected = code === activeLanguage.code

                    return (
                        <MenuItem
                            key={code}
                            className={styles.menuItem}
                            selected={isSelected}
                            onClick={() => selectLanguage(code)}
                            role="option"
                            aria-selected={isSelected}
                        >
                            <img
                                className={styles.flag}
                                src={flag}
                                alt=""
                                aria-hidden="true"
                            />
                            <span className={styles.menuItemName}>{name}</span>
                            {isSelected && (
                                <CheckIcon className={styles.checkIcon} />
                            )}
                        </MenuItem>
                    )
                })}
            </Menu>
        </>
    )
}

export default LanguageSelector
